import React, { useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import classnames from 'classnames';
import _get from 'lodash/get';
import { Query, QueryResult } from 'react-apollo';
import accountDetailsQuery from 'queries/group/accountDetails.gql';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import * as styles from 'components/Settings/Sections.scss';
import AccountSupport from './AccountSupport';
import FreemiumUpgradeLink from 'components/FreemiumUpgradeLink/FreemiumUpgradeLink';
import {
  selectEcommerceLoading,
  selectEcommercePlan,
  selectEcommerceSupported
} from 'selectors/ecommerceSelector';
import { useSelector } from 'react-redux';

const LabelData = ({
  label,
  isBold,
  used,
  total,
  unlimited,
  children,
}: {
  label: string;
  isBold?: boolean;
  used?: number | null;
  total?: number | null;
  unlimited?: boolean;
  children?: React.ReactNode;
}) => {
  const renderData = () => {
    if (children) {
      return children;
    }
    if (total === null || (total === 0 && !unlimited)) {
      return intl.get('notIncluded');
    }
    if (total === 0) {
      return `${used } / ${intl.get('unlimited')}`;
    }
    return `${used} / ${total === 0 ? intl.get('unlimited') : total}`;
  };

  return (
    <dl>
      <dt className={classnames(isBold && styles.bold)}>
        {label}:
      </dt>
      <dd>
        {renderData()}
      </dd>
    </dl>
  );
};

type UsageStats = {
  used: number | null;
  total: number | null;
};

type Props = {
  fetchData: () => Promise<{}>;
  usersTotal: number;
  meetingsTotal: number;
  recordingHours: UsageStats;
  status: string | null;
  impersonationFeatureEnabled: boolean;
  isImpersonating: boolean;
};

const AccountDetails = (props: Props) => {
  const {
    fetchData,
    status,
    usersTotal,
    meetingsTotal,
    recordingHours,
    impersonationFeatureEnabled,
    isImpersonating
  } = props;
  const [dataLoading, setDataLoading] = useState(true);
  const [accountType, setAccountType] = useState(status);
  const ecommercePlan = useSelector(selectEcommercePlan);
  const isEcommerceSupported = useSelector(selectEcommerceSupported);
  const ecommerceAccountLoading = useSelector(selectEcommerceLoading);

  useEffect(() => {
    if (ecommercePlan && isEcommerceSupported) {
      setAccountType(ecommercePlan);
    }
  },        [ecommercePlan, isEcommerceSupported]);

  useEffect(
    () => {
      const handleDataFetch = async () => {
        await fetchData();
        setDataLoading(false);
      };
      handleDataFetch();
    },
    []
  );

  const now = new Date();
  const queryVariables = {
    dateStart: now.toISOString().split('T')[0],
    dateEnd: now.toISOString().split('T')[0]
  };

  return (
    <>
      <Query query={accountDetailsQuery} variables={{ ...queryVariables }} fetchPolicy="network-only">
        {({ loading, error, data }: QueryResult<object>) => {
          return loading || dataLoading || ecommerceAccountLoading ? (
            <LoadingIndicator />
          ) : (
            <section className={styles.container} data-test="settingsSection.plan">
              <article>
                <h6 className={classnames(styles.title, styles.headerRow)} data-test="sectionTitle">
                  {intl.get('accountPlan')}
                  <div className={styles.legend}>{intl.get('activeOfTotal')}</div>
                </h6>
                <div className={styles.flexRow}>
                  <LabelData
                    label={intl.get('users')}
                    used={_get(data, 'accountSummary.totalUsersCount', 1)}
                    total={usersTotal}
                    unlimited={true}
                  />
                  <LabelData
                    label={intl.get('permanentMeetings')}
                    used={_get(data, 'myGroup.settings.meetings.currentMeetingCount', 0)}
                    total={meetingsTotal}
                    unlimited={true}
                  />
                  <LabelData
                    label={intl.get('recordingHours')}
                    {...recordingHours}
                    unlimited={true}
                  />
                </div>
              </article>
              <article>
                <div className={styles.flexColumn}>
                  <LabelData label={intl.get('status')} isBold={true}>
                    {accountType}
                  </LabelData>
                  {isEcommerceSupported && <FreemiumUpgradeLink />}
                </div>
              </article>
            </section>
          );
        }}
      </Query>
      {impersonationFeatureEnabled && !isImpersonating && <AccountSupport />}
    </>
  );
};

export default AccountDetails;
